import { TERMINAL_STATUSES } from '../types';
import type {
  ChannelKind,
  LucideIconName,
  MockCache,
  Channel,
  Task,
  UserId,
  TaskId,
} from '../types';

export function asRecord(row: object): Record<string, unknown> {
  return row as unknown as Record<string, unknown>;
}

export function snapshot(row: object): Record<string, unknown> {
  return asRecord(structuredClone(row));
}

const SORT_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';

export function sortKeyBetween(before: string | null, after: string | null): string {
  const base = SORT_ALPHABET.length;
  let out = '';
  for (let i = 0; i < 64; i += 1) {
    const beforeAt = before !== null && i < before.length ? SORT_ALPHABET.indexOf(before[i] as string) : 0;
    const afterAt = after !== null && i < after.length ? SORT_ALPHABET.indexOf(after[i] as string) : base;
    const lo = beforeAt < 0 ? 0 : beforeAt;
    const hi = afterAt < 0 ? base : afterAt;
    if (hi - lo > 1) {
      return out + SORT_ALPHABET[(lo + hi) >> 1];
    }
    out += SORT_ALPHABET[lo];
  }
  return out + SORT_ALPHABET[base >> 1];
}

export function maxSortKey(keys: string[]): string | null {
  let max: string | null = null;
  for (const key of keys) {
    if (max === null || key > max) {
      max = key;
    }
  }
  return max;
}

export const CHANNEL_ICONS: Record<ChannelKind, LucideIconName> = {
  public: 'Hash' as LucideIconName,
  private: 'Lock' as LucideIconName,
  dm: 'User' as LucideIconName,
  group: 'Users' as LucideIconName,
};

export function channelLabel(cache: MockCache, channel: Channel): string {
  if (channel.name !== null) {
    return channel.name;
  }
  const handles: string[] = [];
  for (const member of cache.members.values()) {
    if (member.channelId === channel.id && handles.length < 3) {
      const user = cache.users.get(member.userId);
      if (user !== undefined) {
        handles.push(`@${user.handle}`);
      }
    }
  }
  return handles.length > 0 ? handles.join(', ') : 'Личка';
}

export function handleMentionedUserIds(cache: MockCache, text: string): UserId[] {
  const found = new Set<UserId>();
  const handlePattern = /@([A-Za-z0-9_]+)/g;
  let handleMatch: RegExpExecArray | null;
  while ((handleMatch = handlePattern.exec(text)) !== null) {
    const handle = handleMatch[1] as string;
    for (const user of cache.users.values()) {
      if (user.handle === handle && user.active) {
        found.add(user.id);
      }
    }
  }
  return [...found];
}

export function refreshMentions(
  cache: MockCache,
  sourceKind: 'task' | 'comment' | 'message',
  sourceId: string,
  text: string,
): void {
  cache.mentions = cache.mentions.filter(
    (mention) => !(mention.sourceKind === sourceKind && mention.sourceId === sourceId),
  );
  const now = cache.nowMs();
  const seen = new Set<string>();
  for (const userId of handleMentionedUserIds(cache, text)) {
    cache.mentions.push({
      sourceKind,
      sourceId,
      targetKind: 'subject',
      targetId: String(userId),
      createdAt: now,
    });
  }
  const keyPattern = /\b([A-Z]{2,6})-(\d+)\b/g;
  let keyMatch: RegExpExecArray | null;
  while ((keyMatch = keyPattern.exec(text)) !== null) {
    const key = `${keyMatch[1]}-${keyMatch[2]}`;
    const taskId = cache.index.taskByKey.get(key as Task['key']);
    if (taskId !== undefined) {
      const dedupe = `task:${String(taskId)}`;
      if (!seen.has(dedupe)) {
        seen.add(dedupe);
        cache.mentions.push({
          sourceKind,
          sourceId,
          targetKind: 'task',
          targetId: String(taskId),
          createdAt: now,
        });
      }
    }
  }
}

export function mentionedUserIds(cache: MockCache, sourceKind: 'comment' | 'message', sourceId: string): UserId[] {
  const ids: UserId[] = [];
  for (const mention of cache.mentions) {
    if (
      mention.sourceKind === sourceKind &&
      mention.sourceId === sourceId &&
      mention.targetKind === 'subject'
    ) {
      ids.push(mention.targetId as unknown as UserId);
    }
  }
  return ids;
}

export function isTerminal(status: string): boolean {
  return (TERMINAL_STATUSES as readonly string[]).includes(status);
}

export function taskKeys(cache: MockCache, taskId: TaskId): string[] {
  const task = cache.tasks.get(taskId);
  const keys = [`task:${String(taskId)}`];
  if (task !== undefined) {
    keys.push(`board:${String(task.projectId)}`);
    if (task.parentId !== null) {
      keys.push(`task:${String(task.parentId)}`);
    }
  }
  return keys;
}
